import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import apiClient from '../utils/api';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Search, Trash2, Eye, EyeOff, CheckCircle, XCircle, Star } from 'lucide-react';
import { useAuthStore } from '../store/authStore';
import { toast } from 'sonner';

export default function ManageAllArtists() {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [artists, setArtists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('all');

  useEffect(() => {
    if (!user) return;
    if (user.role !== 'admin' && user.role !== 'owner' && user.role !== 'overseer') {
      navigate('/dashboard');
      return;
    }
    fetchArtists();
  }, [user, activeTab, navigate]);

  const fetchArtists = async () => {
    try {
      setLoading(true);
      // Use admin endpoint to get all artists including hidden ones
      const response = await apiClient.get('/admin/artists/all');
      let filteredArtists = response.data;
      
      if (activeTab === 'approved') {
        filteredArtists = response.data.filter(a => a.status === 'approved');
      } else if (activeTab === 'pending') {
        filteredArtists = response.data.filter(a => a.status === 'pending');
      } else if (activeTab === 'hidden') {
        filteredArtists = response.data.filter(a => a.is_hidden === true);
      }
      
      setArtists(filteredArtists);
    } catch (error) {
      console.error('Error fetching artists:', error);
      // Fallback to old method if admin endpoint fails
      try {
        const [approvedRes, pendingRes] = await Promise.all([
          apiClient.get('/artists?status=approved&include_hidden=true'),
          apiClient.get('/admin/artists/pending')
        ]);
        setArtists([...approvedRes.data, ...pendingRes.data]);
      } catch (e) {
        toast.error('Failed to fetch artists');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (artistId, status) => {
    try {
      await apiClient.put(`/admin/artists/${artistId}/status?status=${status}`);
      toast.success(`Artist ${status} successfully!`);
      fetchArtists();
    } catch (error) {
      console.error('Error updating artist status:', error);
      toast.error('Failed to update artist status');
    }
  };

  const handleToggleVisibility = async (artistId, currentlyHidden) => {
    try {
      await apiClient.put(`/admin/artists/${artistId}/visibility`);
      toast.success(`Artist ${currentlyHidden ? 'unhidden' : 'hidden'} successfully!`);
      fetchArtists();
    } catch (error) {
      console.error('Error toggling visibility:', error);
      toast.error('Failed to update artist visibility');
    }
  };

  const handleDelete = async (artistId) => {
    if (!window.confirm('Are you sure you want to delete this artist? This action cannot be undone.')) {
      return;
    }
    try {
      await apiClient.put(`/admin/artists/${artistId}/status?status=rejected`);
      toast.success('Artist removed successfully!');
      fetchArtists();
    } catch (error) {
      console.error('Error deleting artist:', error);
      toast.error('Failed to delete artist');
    }
  };

  const filteredArtists = artists.filter(
    (artist) =>
      artist.bio?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      artist.genres?.some((g) => g.toLowerCase().includes(searchTerm.toLowerCase())) ||
      artist.artist_id?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getStatusColor = (status) => {
    switch (status) {
      case 'approved':
        return 'bg-green-500/20 text-green-300';
      case 'pending':
        return 'bg-yellow-500/20 text-yellow-300';
      case 'rejected':
        return 'bg-red-500/20 text-red-300';
      default:
        return 'bg-gray-500/20 text-gray-300';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-16 h-16 border-4 border-violet-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen relative pt-20">
      <div className="container mx-auto px-4 py-8 relative z-10 max-w-7xl">
        <div className="mb-8">
          <h1 className="text-4xl font-bold gradient-text mb-2">Manage All Artists</h1>
          <p className="text-white/60">View, approve, hide/unhide, and manage all artist profiles</p>
        </div>

        {/* Search Bar */}
        <div className="glass-default rounded-2xl p-4 mb-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-white/40" size={20} />
            <Input
              data-testid="search-artists"
              type="text"
              placeholder="Search by name, genre, or artist ID..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 bg-white/5 border-white/10 focus:border-violet-500 text-white placeholder:text-white/30"
            />
          </div>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="glass-default border border-white/10 p-1">
            <TabsTrigger value="all" className="data-[state=active]:bg-violet-600">
              All ({artists.length})
            </TabsTrigger>
            <TabsTrigger value="approved" className="data-[state=active]:bg-green-600">
              Approved
            </TabsTrigger>
            <TabsTrigger value="pending" className="data-[state=active]:bg-yellow-600">
              Pending
            </TabsTrigger>
            <TabsTrigger value="hidden" className="data-[state=active]:bg-gray-600">
              Hidden
            </TabsTrigger>
          </TabsList>

          <TabsContent value={activeTab} className="space-y-4">
            {filteredArtists.length === 0 ? (
              <Card className="glass-default border-white/10">
                <CardContent className="p-12 text-center">
                  <p className="text-xl text-white/60">No artists found</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 gap-4">
                {filteredArtists.map((artist) => (
                  <Card
                    key={artist.artist_id}
                    data-testid={`artist-card-${artist.artist_id}`}
                    className={`glass-default border-white/10 hover:border-white/20 transition-all ${artist.is_hidden ? 'opacity-60' : ''}`}
                  >
                    <CardContent className="p-6">
                      <div className="flex items-start gap-6">
                        {/* Artist Image */}
                        <div className="w-24 h-24 rounded-lg overflow-hidden bg-gradient-to-br from-violet-500/20 to-fuchsia-500/20 flex-shrink-0 relative">
                          {artist.media_urls?.[0] ? (
                            <img
                              src={artist.media_urls[0]}
                              alt="Artist"
                              className="w-full h-full object-cover"
                              onError={(e) => {
                                e.target.style.display = 'none';
                              }}
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <Star className="text-violet-400/50" size={32} />
                            </div>
                          )}
                          {artist.is_hidden && (
                            <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                              <EyeOff className="text-white/70" size={24} />
                            </div>
                          )}
                        </div>

                        {/* Artist Info */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-4 mb-3">
                            <div>
                              <div className="flex items-center gap-3 mb-2">
                                <h3 className="text-xl font-bold">{artist.bio?.split(' ').slice(0, 4).join(' ')}</h3>
                                <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(artist.status)}`}>
                                  {artist.status}
                                </span>
                                {artist.is_hidden && (
                                  <span className="px-3 py-1 rounded-full text-xs font-medium bg-gray-500/30 text-gray-300">
                                    Hidden
                                  </span>
                                )}
                              </div>
                              <p className="text-sm text-white/60 mb-2 line-clamp-2">{artist.bio}</p>
                              <p className="text-xs text-white/40 font-mono">ID: {artist.artist_id}</p>
                            </div>
                          </div>

                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                            <div>
                              <p className="text-xs text-white/60 mb-1">Genres</p>
                              <div className="flex flex-wrap gap-1">
                                {artist.genres?.map((genre, idx) => (
                                  <span key={idx} className="text-xs px-2 py-1 rounded-full bg-violet-500/20 text-violet-300">
                                    {genre}
                                  </span>
                                ))}
                              </div>
                            </div>
                            <div>
                              <p className="text-xs text-white/60 mb-1">Location</p>
                            </div>
                            <div>
                              <p className="text-xs text-white/60 mb-1">Stats</p>
                              <div className="flex items-center gap-2">
                                <Star className="text-yellow-400" size={14} />
                                <span className="text-sm font-medium">{artist.rating?.toFixed(1) || '0.0'}</span>
                                <span className="text-xs text-white/60">({artist.total_bookings || 0} bookings)</span>
                              </div>
                            </div>
                          </div>

                          {/* Actions */}
                          <div className="flex items-center gap-2 flex-wrap">
                            <Button
                              data-testid={`view-artist-${artist.artist_id}`}
                              onClick={() => navigate(`/artist/${artist.artist_id}`)}
                              size="sm"
                              variant="outline"
                              className="border-white/20 hover:bg-white/10"
                            >
                              <Eye size={16} className="mr-1" />
                              View
                            </Button>

                            {/* Hide/Unhide Button */}
                            <Button
                              data-testid={`toggle-visibility-${artist.artist_id}`}
                              onClick={() => handleToggleVisibility(artist.artist_id, artist.is_hidden)}
                              size="sm"
                              variant="outline"
                              className={artist.is_hidden 
                                ? "border-green-500/50 text-green-400 hover:bg-green-500/10" 
                                : "border-orange-500/50 text-orange-400 hover:bg-orange-500/10"
                              }
                            >
                              {artist.is_hidden ? (
                                <>
                                  <Eye size={16} className="mr-1" />
                                  Unhide
                                </>
                              ) : (
                                <>
                                  <EyeOff size={16} className="mr-1" />
                                  Hide
                                </>
                              )}
                            </Button>

                            {artist.status === 'pending' && (
                              <>
                                <Button
                                  data-testid={`approve-artist-${artist.artist_id}`}
                                  onClick={() => handleStatusChange(artist.artist_id, 'approved')}
                                  size="sm"
                                  className="bg-green-600 hover:bg-green-500"
                                >
                                  <CheckCircle size={16} className="mr-1" />
                                  Approve
                                </Button>
                                <Button
                                  data-testid={`reject-artist-${artist.artist_id}`}
                                  onClick={() => handleStatusChange(artist.artist_id, 'rejected')}
                                  size="sm"
                                  variant="outline"
                                  className="border-red-500/50 text-red-400 hover:bg-red-500/10"
                                >
                                  <XCircle size={16} className="mr-1" />
                                  Reject
                                </Button>
                              </>
                            )}

                            {artist.status === 'approved' && (
                              <Button
                                data-testid={`remove-artist-${artist.artist_id}`}
                                onClick={() => handleDelete(artist.artist_id)}
                                size="sm"
                                variant="outline"
                                className="border-red-500/50 text-red-400 hover:bg-red-500/10"
                              >
                                <Trash2 size={16} className="mr-1" />
                                Remove
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
